import React, { useState, useEffect } from 'react';
import {
  Settings, Coins, Wallet, Inbox, Users, BarChart3, Plus, Edit2, Trash2,
  Check, X, RefreshCw, AlertTriangle, Zap
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useBinancePrices } from '../../hooks/useBinancePrices';

const ADMIN_TABS = [
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'assets', label: 'Assets', icon: Coins },
  { id: 'deposits', label: 'Deposits', icon: Inbox },
  { id: 'system_wallets', label: 'System Wallets', icon: Wallet },
  { id: 'mlm', label: 'Affiliate Network Tree', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const AVXAdmin = () => {
  const [tab, setTab] = useState('stats');

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="avx-admin-page">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">AVX Admin Panel</h1>

        <div className="sticky top-24 md:top-28 z-30 -mx-4 md:-mx-6 px-4 md:px-6 bg-slate-950/95 backdrop-blur flex gap-1 overflow-x-auto border-b border-slate-800 mb-6">
          {ADMIN_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`avx-admin-tab-${t.id}`}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium border-b-2 whitespace-nowrap ${
                  tab === t.id ? 'border-yellow-400 text-yellow-300' : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'stats' && <StatsTab />}
        {tab === 'assets' && <AssetsTab />}
        {tab === 'deposits' && <DepositsTab />}
        {tab === 'withdrawals' && <WithdrawalsTab />}
        {tab === 'system_wallets' && <SystemWalletsTab />}
        {tab === 'mlm' && <AffiliateNetworkTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
};

// ============== STATS ==============
const StatsTab = () => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    Promise.all([api.get('/admin/avx/stats'), api.get('/admin/avx/settings')]).then(([s, st]) => {
      setStats(s.data);
      setSettings(st.data);
    });
  }, []);
  if (!stats || !settings) return <div>Loading...</div>;

  const fd = settings.fee_distribution || {};
  const lvl = settings.mlm_levels || {};
  const buckets = [
    { key: 'mlm', label: 'Affiliate Network (7 levels)', color: 'from-purple-500 to-pink-500' },
    { key: 'promo', label: 'Promo', color: 'from-blue-500 to-cyan-500' },
    { key: 'govt_tax', label: 'Govt Tax', color: 'from-red-500 to-orange-500' },
    { key: 'social_work', label: 'Social Work', color: 'from-emerald-500 to-teal-500' },
    { key: 'admin', label: 'Admin', color: 'from-yellow-500 to-amber-500' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total Users (with wallet)" value={stats.total_users} />
        <Stat label="Total Trades" value={stats.total_orders} />
        <Stat label="Total P2P" value={stats.total_p2p} />
        <Stat label="Pending Deposits" value={stats.pending_deposits} highlight />
      </div>

      <div>
        <h3 className="font-semibold mb-2">System Wallets (4 pools — collected from fees)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.system_wallets.map(w => (
            <div key={w.type} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-xs text-slate-400 capitalize">{w.type.replace('_', ' ')}</div>
              <div className="text-xl font-bold text-yellow-300 mt-1">{w.balance.toFixed(2)} AT</div>
              <div className="text-xs text-slate-500 mt-1">Total credited: {w.total_credited.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Fee Distribution Visualization */}
      <div className="bg-slate-800/40 border border-yellow-500/30 rounded-xl p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <span>📊</span> 100% Fee Distribution Map
        </h3>
        <p className="text-xs text-slate-400 mb-3">
          Trading fee: <strong className="text-yellow-300">{settings.trading_fee_percent}%</strong> • P2P fee: <strong className="text-yellow-300">{settings.p2p_fee_percent}%</strong> • Distribution sums to 100%
        </p>
        <div className="flex h-12 rounded-lg overflow-hidden mb-2 border border-slate-700">
          {buckets.map((b) => (
            <div
              key={b.key}
              className={`bg-gradient-to-r ${b.color} flex items-center justify-center text-xs font-bold text-white`}
              style={{ width: `${fd[b.key] || 0}%` }}
              title={`${b.label}: ${fd[b.key]}%`}
            >
              {(fd[b.key] || 0) >= 7 ? `${fd[b.key]}%` : ''}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-slate-300">
          {buckets.map((b) => (
            <span key={b.key} className="flex items-center gap-1">
              <span className={`w-3 h-3 rounded-sm bg-gradient-to-r ${b.color}`}></span>
              {b.label}: <strong>{fd[b.key] || 0}%</strong>
            </span>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-700">
          <h4 className="text-sm font-semibold text-purple-300 mb-2">Affiliate Network 7-Level (out of {fd.mlm}% Affiliate Network share)</h4>
          <div className="grid grid-cols-7 gap-1">
            {[1, 2, 3, 4, 5, 6, 7].map(l => (
              <div key={l} className="bg-purple-500/20 border border-purple-500/40 rounded p-2 text-center">
                <div className="text-xs text-purple-300">L{l}</div>
                <div className="text-sm font-bold text-white">{lvl[l] || lvl[String(l)] || 0}%</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className={`bg-slate-800/50 border ${highlight ? 'border-yellow-500/40' : 'border-slate-700'} rounded-lg p-4`}>
    <div className="text-xs text-slate-400">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

// ============== ASSETS ==============
const AssetsTab = () => {
  const [assets, setAssets] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Live Binance prices for assets that have binance_symbol
  const binanceSymbols = assets.filter(a => a.binance_symbol).map(a => a.binance_symbol);
  const { prices: livePrices } = useBinancePrices(binanceSymbols, 15000);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/avx/assets');
    setAssets(r.data?.assets || []);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete asset?')) return;
    await api.delete(`/admin/avx/assets/${id}`);
    toast.success('Deleted');
    load();
  };

  const syncOne = async (asset) => {
    const live = livePrices[asset.binance_symbol];
    if (!live?.price) {
      toast.error(`Live price for ${asset.binance_symbol} not loaded yet. Wait a few seconds.`);
      return;
    }
    try {
      const r = await api.post(`/admin/avx/assets/${asset.id}/sync-rate`, { price_usd: live.price });
      toast.success(`${asset.symbol}: ${r.data.old_rate_at} → ${r.data.new_rate_at} AT`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    }
  };

  const syncAll = async () => {
    const liveAssets = assets.filter(a => a.binance_symbol);
    if (liveAssets.length === 0) {
      toast.error('कोई asset Binance से linked नहीं है। पहले "Auto-Detect Symbols" दबाएँ।', { duration: 5000 });
      return;
    }
    const prices = {};
    for (const a of liveAssets) {
      const p = livePrices[a.binance_symbol]?.price;
      if (p) prices[a.binance_symbol] = p;
    }
    if (Object.keys(prices).length === 0) {
      return toast.error('Live prices अभी load हो रहे हैं। 10 sec wait करके दोबारा try करें।', { duration: 4000 });
    }
    setSyncing(true);
    try {
      const r = await api.post('/admin/avx/sync-all-rates', { prices });
      toast.success(`✓ Synced ${r.data.synced_count} assets from Binance`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const autoDetect = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/admin/avx/auto-detect-symbols');
      const cnt = r.data?.updated_count || 0;
      if (cnt === 0) {
        toast.info('All assets already linked, या कोई match नहीं मिला।');
      } else {
        toast.success(`✓ ${cnt} assets के Binance symbols auto-set हो गए। अब "Sync All" दबाएँ।`, { duration: 5000 });
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-detect failed');
    } finally {
      setSyncing(false);
    }
  };

  const fmtAge = (iso) => {
    if (!iso) return 'never';
    const mins = (Date.now() - new Date(iso).getTime()) / 60000;
    if (mins < 1) return 'just now';
    if (mins < 60) return `${Math.floor(mins)}m ago`;
    if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
    return `${Math.floor(mins / 1440)}d ago`;
  };

  const setManualPrice = async (asset) => {
    const current = asset.current_rate || 0;
    const input = window.prompt(
      `${asset.symbol} (${asset.name}) का नया AT rate enter करें (current: ${current} AT):\n\nMetals/commodities के लिए: actual ₹ price डालें\nजैसे 10g Gold = 75000 AT (= ₹75000)\nजैसे 1kg Onion = 40 AT (= ₹40)`,
      String(current)
    );
    if (!input) return;
    const newRate = parseFloat(input);
    if (!newRate || newRate <= 0) return toast.error('Invalid rate');
    const note = window.prompt('Source/Note (optional):', '') || '';
    try {
      const r = await api.post(`/admin/avx/assets/${asset.id}/manual-price`, {
        new_rate_at: newRate,
        note: note,
      });
      toast.success(`${asset.symbol}: ${r.data.old_rate_at} → ${r.data.new_rate_at} AT`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Manual price set failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          data-testid="add-asset-btn"
          className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Add Asset
        </button>
        <button
          onClick={autoDetect}
          disabled={syncing}
          data-testid="auto-detect-btn"
          className="px-4 py-2 bg-purple-600 text-white font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          🔍 Auto-Detect Binance Symbols
        </button>
        <button
          onClick={syncAll}
          disabled={syncing}
          data-testid="sync-all-btn"
          className="px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          <Zap className="w-4 h-4" /> {syncing ? 'Syncing...' : 'Sync All Live Rates'}
        </button>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200">
        <strong>📖 कैसे use करें:</strong>
        <ol className="list-decimal list-inside mt-1 space-y-0.5">
          <li>Add Asset (BTC, ETH, etc.) — Binance Symbol field को खाली छोड़ सकते हैं</li>
          <li><strong>"Auto-Detect Binance Symbols"</strong> दबाएँ — server खुद BTC→BTCUSDT detect कर देगा</li>
          <li><strong>"Sync All Live Rates"</strong> दबाएँ — live prices fetch होकर AT rate update हो जाएगा</li>
          <li>10-15 second wait करें page पर — Binance prices auto-fetch होती हैं browser में</li>
        </ol>
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800 text-slate-300">
            <tr>
              <th className="text-left p-3">Symbol</th>
              <th className="text-left p-3">Name</th>
              <th className="text-right p-3">AT Rate</th>
              <th className="text-right p-3">Live USD</th>
              <th className="text-right p-3">24h %</th>
              <th className="text-left p-3">Last Sync</th>
              <th className="text-center p-3">Active</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {assets.map(a => {
              const live = a.binance_symbol ? livePrices[a.binance_symbol] : null;
              const change = live?.change_24h_pct;
              return (
                <tr key={a.id} className="border-t border-slate-700">
                  <td className="p-3 font-bold flex items-center gap-1">
                    {a.symbol}
                    {a.binance_symbol && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                  </td>
                  <td className="p-3">{a.name}<div className="text-xs text-slate-500">{a.binance_symbol || a.category}</div></td>
                  <td className="p-3 text-right text-yellow-300">{a.current_rate}</td>
                  <td className="p-3 text-right text-slate-300">{live ? `$${live.price.toLocaleString()}` : '-'}</td>
                  <td className={`p-3 text-right ${change >= 0 ? 'text-emerald-400' : change < 0 ? 'text-red-400' : 'text-slate-500'}`}>
                    {live ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}%` : '-'}
                  </td>
                  <td className="p-3 text-xs text-slate-400">{a.binance_symbol ? fmtAge(a.last_synced_at) : '—'}</td>
                  <td className="p-3 text-center">{a.is_active ? <Check className="w-4 h-4 text-emerald-400 inline" /> : <X className="w-4 h-4 text-red-400 inline" />}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setManualPrice(a)}
                      title="Manual price set (for metals, commodities, etc.)"
                      data-testid={`manual-price-${a.symbol}`}
                      className="p-1 hover:bg-orange-700/30 rounded mr-1 text-orange-400 font-bold text-sm"
                    >
                      ₹
                    </button>
                    {a.binance_symbol && (
                      <button onClick={() => syncOne(a)} title="Sync rate from CoinGecko" className="p-1 hover:bg-emerald-700/30 rounded mr-1">
                        <Zap className="w-4 h-4 text-emerald-400" />
                      </button>
                    )}
                    <button onClick={() => { setEditing(a); setShowModal(true); }} className="p-1 hover:bg-slate-700 rounded mr-1">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(a.id)} className="p-1 hover:bg-red-700/30 rounded">
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && <AssetModal editing={editing} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); load(); }} />}
    </div>
  );
};

const AssetModal = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    symbol: editing?.symbol || '',
    name: editing?.name || '',
    category: editing?.category || 'custom',
    current_rate: editing?.current_rate || 1,
    total_quantity: editing?.total_quantity || 1000000,
    icon_url: editing?.icon_url || '',
    description: editing?.description || '',
    is_active: editing?.is_active !== false,
    rate_override: editing?.rate_override || false,
    binance_symbol: editing?.binance_symbol || '',
    tradingview_symbol: editing?.tradingview_symbol || '',
    auto_price_sync: editing?.auto_price_sync || false,
  });

  const submit = async () => {
    if (!form.symbol || !form.name) return toast.error('Symbol and Name required');
    try {
      if (editing) {
        await api.put(`/admin/avx/assets/${editing.id}`, form);
      } else {
        await api.post('/admin/avx/assets', form);
      }
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4">{editing ? 'Edit Asset' : 'Add Asset'}</h3>
        <div className="space-y-3">
          <input data-testid="asset-symbol" placeholder="Symbol (e.g. GOLD)" value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />
          <input data-testid="asset-name" placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />
          <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded">
            <option value="custom">Custom</option>
            <option value="crypto">Crypto</option>
            <option value="metal">Metal</option>
            <option value="stock">Stock</option>
            <option value="forex">Forex</option>
          </select>
          <input type="number" data-testid="asset-rate" placeholder="Rate (AT)" value={form.current_rate} onChange={e => setForm({...form, current_rate: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />
          <input type="number" placeholder="Total quantity" value={form.total_quantity} onChange={e => setForm({...form, total_quantity: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />
          <input placeholder="Icon URL (optional)" value={form.icon_url} onChange={e => setForm({...form, icon_url: e.target.value})} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded" />

          <div className="border-t border-slate-700 pt-3 mt-3">
            <div className="text-xs font-semibold text-emerald-300 mb-2">📊 Live Market Data (Optional)</div>
            <input
              placeholder="Binance Symbol (e.g. BTCUSDT, ETHUSDT)"
              value={form.binance_symbol}
              onChange={e => setForm({...form, binance_symbol: e.target.value.toUpperCase()})}
              data-testid="asset-binance-symbol"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mb-2"
            />
            <input
              placeholder="TradingView Symbol (e.g. BINANCE:BTCUSDT, NASDAQ:AAPL)"
              value={form.tradingview_symbol}
              onChange={e => setForm({...form, tradingview_symbol: e.target.value})}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mb-2"
            />
            <p className="text-xs text-slate-400">
              अगर Binance symbol set किया तो users को live price + 24h change दिखेगा। TradingView symbol से advanced chart आएगा (खाली रखने पर BINANCE: prefix automatic लगेगा)।
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
            Active
          </label>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 bg-slate-700 rounded">Cancel</button>
          <button onClick={submit} data-testid="asset-save-btn" className="flex-1 py-2 bg-yellow-500 text-black font-semibold rounded">Save</button>
        </div>
      </div>
    </div>
  );
};

// ============== DEPOSITS ==============
const DepositsTab = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');

  useEffect(() => { load(); }, [filter]);
  const load = async () => {
    const r = await api.get(`/admin/avx/deposits?status=${filter}`);
    setRequests(r.data?.requests || []);
  };

  const action = async (id, act) => {
    const note = window.prompt(`${act === 'approve' ? 'Approve' : 'Reject'} note (optional):`);
    try {
      await api.post(`/admin/avx/deposits/${id}/action`, { action: act, admin_note: note || '' });
      toast.success(act === 'approve' ? 'Approved & user wallet credited' : 'Rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1 rounded text-sm ${filter === s ? 'bg-yellow-500 text-black' : 'bg-slate-800 text-slate-300'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700">
        {requests.map(r => (
          <div key={r.id} className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-bold">₹{r.amount} • {r.user_username}</div>
              <div className="text-xs text-slate-400">{r.user_email} • {r.payment_method} • UTR: {r.payment_proof || 'N/A'}</div>
              {r.note && <div className="text-xs text-slate-400 mt-1">Note: "{r.note}"</div>}
              <div className="text-xs text-slate-600 mt-1">{new Date(r.created_at).toLocaleString()}</div>
            </div>
            {r.status === 'pending' ? (
              <div className="flex gap-2">
                <button onClick={() => action(r.id, 'approve')} data-testid={`approve-${r.id}`} className="px-3 py-1 bg-emerald-600 rounded text-sm font-semibold">Approve</button>
                <button onClick={() => action(r.id, 'reject')} className="px-3 py-1 bg-red-600 rounded text-sm font-semibold">Reject</button>
              </div>
            ) : (
              <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{r.status}</span>
            )}
          </div>
        ))}
        {requests.length === 0 && <div className="p-8 text-center text-slate-500">No {filter} deposits</div>}
      </div>
    </div>
  );
};

// ============== WITHDRAWALS ==============
const WithdrawalsTab = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');

  useEffect(() => { load(); }, [filter]);
  const load = async () => {
    const r = await api.get(`/admin/avx/withdrawals?status=${filter}`);
    setRequests(r.data?.requests || []);
  };

  const action = async (id, act) => {
    let payoutRef = '';
    let note = '';
    if (act === 'approve') {
      payoutRef = window.prompt('Payout reference / UTR (optional but recommended):') || '';
    }
    note = window.prompt(`${act === 'approve' ? 'Approve' : 'Reject'} note (optional):`) || '';
    try {
      await api.post(`/admin/avx/withdrawals/${id}/action`, {
        action: act, admin_note: note, payout_reference: payoutRef,
      });
      toast.success(act === 'approve' ? 'Approved (mark paid)' : 'Rejected & user refunded');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1 rounded text-sm ${filter === s ? 'bg-yellow-500 text-black' : 'bg-slate-800 text-slate-300'}`}>
            {s}
          </button>
        ))}
      </div>

      <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700">
        {requests.map(r => (
          <div key={r.id} className="p-4 flex items-start justify-between flex-wrap gap-3">
            <div className="flex-1">
              <div className="font-bold">₹{r.amount.toLocaleString()} • {r.user_username}</div>
              <div className="text-xs text-slate-400 mt-1">
                {r.method.toUpperCase()} • {r.method === 'bank' ? `${r.account_holder} | A/c: ${r.account_number} | IFSC: ${r.ifsc}` : `UPI: ${r.upi_id}`}
              </div>
              <div className="text-xs text-slate-500 mt-1">{r.user_email}</div>
              {r.note && <div className="text-xs text-slate-400 mt-1">Note: "{r.note}"</div>}
              {r.payout_reference && <div className="text-xs text-emerald-400 mt-1">Payout Ref: {r.payout_reference}</div>}
              <div className="text-xs text-slate-600 mt-1">{new Date(r.created_at).toLocaleString()}</div>
            </div>
            {r.status === 'pending' ? (
              <div className="flex gap-2">
                <button onClick={() => action(r.id, 'approve')} data-testid={`approve-withdraw-${r.id}`} className="px-3 py-1 bg-emerald-600 rounded text-sm font-semibold">Mark Paid</button>
                <button onClick={() => action(r.id, 'reject')} className="px-3 py-1 bg-red-600 rounded text-sm font-semibold">Reject (refund)</button>
              </div>
            ) : (
              <span className={`text-xs px-2 py-1 rounded ${r.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{r.status}</span>
            )}
          </div>
        ))}
        {requests.length === 0 && <div className="p-8 text-center text-slate-500">No {filter} withdrawals</div>}
      </div>
    </div>
  );
};

// ============== SYSTEM WALLETS ==============
const SystemWalletsTab = () => {
  const [wallets, setWallets] = useState([]);
  const [logs, setLogs] = useState({ wallet: null, items: [] });

  useEffect(() => { api.get('/admin/avx/system-wallets').then(r => setWallets(r.data?.wallets || [])); }, []);

  const showLogs = async (wt) => {
    const r = await api.get(`/admin/avx/system-wallets/${wt}/logs`);
    setLogs({ wallet: wt, items: r.data?.logs || [] });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {wallets.map(w => (
          <div key={w.type} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
            <div className="text-xs text-slate-400 capitalize">{w.type.replace('_', ' ')}</div>
            <div className="text-2xl font-bold text-yellow-300 mt-1">{w.balance.toFixed(2)} AT</div>
            <div className="text-xs text-slate-500 mt-1">Credited: {w.total_credited.toFixed(2)}</div>
            <button onClick={() => showLogs(w.type)} className="mt-2 text-xs underline text-blue-400">View logs</button>
          </div>
        ))}
      </div>

      {logs.wallet && (
        <div>
          <h3 className="font-semibold mb-2 capitalize">Logs: {logs.wallet.replace('_', ' ')}</h3>
          <div className="bg-slate-800/40 border border-slate-700 rounded-xl divide-y divide-slate-700 max-h-96 overflow-y-auto">
            {logs.items.map(l => (
              <div key={l.id} className="p-3 text-sm flex justify-between">
                <div>
                  <div className="font-semibold capitalize">{l.source.replace('_', ' ')}</div>
                  <div className="text-xs text-slate-500">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className={l.direction === 'credit' ? 'text-emerald-400' : 'text-red-400'}>
                  {l.direction === 'credit' ? '+' : '-'}{l.amount.toFixed(2)}
                </div>
              </div>
            ))}
            {logs.items.length === 0 && <div className="p-6 text-center text-slate-500">No logs</div>}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== Affiliate Network TREE ==============
const AffiliateNetworkTab = () => {
  const [username, setUsername] = useState('');
  const [tree, setTree] = useState(null);

  const load = async () => {
    if (!username) return;
    try {
      const r = await api.get(`/admin/avx/mlm-tree?root_username=${username}`);
      setTree(r.data);
    } catch (e) { toast.error('Not found'); }
  };

  const Node = ({ node }) => (
    <div className="ml-4 border-l border-slate-700 pl-4 py-1">
      <div className="text-sm">
        <strong>{node.username}</strong> ({node.name}) <span className="text-xs text-slate-500">L{node.level}</span>
        {node.is_activated && <span className="ml-2 text-xs px-2 py-0.5 bg-emerald-500/20 text-emerald-300 rounded">Activated</span>}
      </div>
      {(node.children || []).map(c => <Node key={c.id} node={c} />)}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Root username" className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded" />
        <button onClick={load} className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded">View Tree</button>
      </div>
      {tree?.root && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-4">
          <div className="font-bold mb-2">Root: {tree.root.username}</div>
          {(tree.tree || []).map(n => <Node key={n.id} node={n} />)}
          {(tree.tree || []).length === 0 && <div className="text-slate-500">No downline</div>}
        </div>
      )}
    </div>
  );
};

// ============== SETTINGS ==============
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/admin/avx/settings');
    setSettings(r.data);
  };

  const save = async () => {
    try {
      await api.put('/admin/avx/settings', settings);
      toast.success('Saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  if (!settings) return <div>Loading...</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Trading Fee (%)" value={settings.trading_fee_percent} onChange={v => setSettings({...settings, trading_fee_percent: parseFloat(v) || 0})} />
        <Field label="P2P Fee (%)" value={settings.p2p_fee_percent} onChange={v => setSettings({...settings, p2p_fee_percent: parseFloat(v) || 0})} />
        <Field label="INR to AT Rate" value={settings.inr_to_at_rate} onChange={v => setSettings({...settings, inr_to_at_rate: parseFloat(v) || 1})} />
        <Field label="USD to INR Rate (forex)" value={settings.usd_to_inr_rate || 83} onChange={v => setSettings({...settings, usd_to_inr_rate: parseFloat(v) || 83})} />
        <Field label="Min Deposit (₹)" value={settings.min_deposit} onChange={v => setSettings({...settings, min_deposit: parseFloat(v) || 0})} />
        <Field label="Min Trade (AT)" value={settings.min_trade} onChange={v => setSettings({...settings, min_trade: parseFloat(v) || 0})} />
        <Field label="Min Withdrawal (₹)" value={settings.min_withdrawal || 5000} onChange={v => setSettings({...settings, min_withdrawal: parseFloat(v) || 0})} />
        <Field label="Max Price Sync Age (mins)" value={settings.max_sync_age_minutes || 60} onChange={v => setSettings({...settings, max_sync_age_minutes: parseInt(v) || 60})} />
      </div>

      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200">
        💡 <strong>USD to INR Rate:</strong> Forex rate (default 83). Live Binance prices (USD) × this rate × INR-to-AT rate = final AT rate. Update daily for accuracy.
        <br />
        <strong>Max Sync Age:</strong> If asset rate is older than this, trading is auto-blocked for safety (anti-arbitrage).
      </div>

      <div>
        <h4 className="font-semibold mb-2">Fee Distribution (must total 100%)</h4>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {['mlm', 'promo', 'govt_tax', 'social_work', 'admin'].map(k => (
            <Field key={k} label={k} value={settings.fee_distribution?.[k] || 0} onChange={v => setSettings({...settings, fee_distribution: { ...settings.fee_distribution, [k]: parseFloat(v) || 0 }})} />
          ))}
        </div>
      </div>

      <div>
        <h4 className="font-semibold mb-2">Affiliate Network Levels (% of fee per level)</h4>
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
          {[1,2,3,4,5,6,7].map(l => (
            <Field key={l} label={`L${l}`} value={settings.mlm_levels?.[l] || 0} onChange={v => setSettings({...settings, mlm_levels: { ...settings.mlm_levels, [l]: parseFloat(v) || 0 }})} />
          ))}
        </div>
      </div>

      <div>
        <label className="text-sm">Fantasy Disclaimer Text</label>
        <textarea
          value={settings.fantasy_disclaimer_text || ''}
          onChange={e => setSettings({...settings, fantasy_disclaimer_text: e.target.value})}
          rows={6}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mt-1"
        />
      </div>

      <div className="flex gap-4 items-center">
        <label className="flex items-center gap-2"><input type="checkbox" checked={settings.is_enabled} onChange={e => setSettings({...settings, is_enabled: e.target.checked})}/> Enabled</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={settings.invite_only} onChange={e => setSettings({...settings, invite_only: e.target.checked})}/> Invite Only</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={settings.fantasy_disclaimer_enabled} onChange={e => setSettings({...settings, fantasy_disclaimer_enabled: e.target.checked})}/> Show Disclaimer</label>
      </div>

      {/* Deposit Payment Details — shown to user on Deposit tab */}
      <div className="border-t border-slate-700 pt-4 mt-4">
        <h4 className="font-semibold mb-1 flex items-center gap-2">💳 Deposit Payment Details</h4>
        <p className="text-xs text-slate-400 mb-3">Users यह info Deposit tab पर देखेंगे. UPI QR image URL upload करके अलग hosting (S3/imgbb/etc) पर डालकर URL paste करें।</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextField label="UPI ID" placeholder="yourname@paytm" value={settings.deposit_upi_id || ''} onChange={v => setSettings({...settings, deposit_upi_id: v})} dataTestid="admin-deposit-upi-id" />
          <TextField label="UPI QR Image URL" placeholder="https://...png" value={settings.deposit_upi_qr_url || ''} onChange={v => setSettings({...settings, deposit_upi_qr_url: v})} dataTestid="admin-deposit-upi-qr" />
          <TextField label="Bank Name" placeholder="HDFC / SBI / ..." value={settings.deposit_bank_name || ''} onChange={v => setSettings({...settings, deposit_bank_name: v})} dataTestid="admin-deposit-bank-name" />
          <TextField label="Account Holder Name" placeholder="ANANT WEB PORTAL" value={settings.deposit_bank_holder || ''} onChange={v => setSettings({...settings, deposit_bank_holder: v})} dataTestid="admin-deposit-bank-holder" />
          <TextField label="Bank Account No." placeholder="50100xxxxxxxxx" value={settings.deposit_bank_account || ''} onChange={v => setSettings({...settings, deposit_bank_account: v})} dataTestid="admin-deposit-bank-account" />
          <TextField label="IFSC Code" placeholder="HDFC0001234" value={settings.deposit_bank_ifsc || ''} onChange={v => setSettings({...settings, deposit_bank_ifsc: v})} dataTestid="admin-deposit-bank-ifsc" />
          <TextField label="Support WhatsApp Number" placeholder="919876543210 (with country code)" value={settings.deposit_support_whatsapp || ''} onChange={v => setSettings({...settings, deposit_support_whatsapp: v})} dataTestid="admin-deposit-whatsapp" />
        </div>
        <div className="mt-3">
          <label className="text-xs text-slate-400">Extra Instructions (optional)</label>
          <textarea
            value={settings.deposit_instructions || ''}
            onChange={e => setSettings({...settings, deposit_instructions: e.target.value})}
            rows={3}
            placeholder="e.g. Payment के बाद WhatsApp पर screenshot ज़रूर भेजें ताकि admin जल्दी approve कर सके।"
            data-testid="admin-deposit-instructions"
            className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mt-1 text-sm"
          />
        </div>
      </div>

      <button onClick={save} data-testid="save-settings-btn" className="px-6 py-2 bg-yellow-500 text-black font-semibold rounded">Save Settings</button>
    </div>
  );
};

const TextField = ({ label, value, onChange, placeholder, dataTestid }) => (
  <div>
    <label className="text-xs text-slate-400">{label}</label>
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={dataTestid}
      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mt-1 text-sm"
    />
  </div>
);

const Field = ({ label, value, onChange }) => (
  <div>
    <label className="text-xs text-slate-400 capitalize">{label.replace('_', ' ')}</label>
    <input type="number" value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded mt-1" />
  </div>
);

export default AVXAdmin;
